import { Jurisdiction } from "./api";

// Customers pick a place, not a legal level: a country and, optionally, a
// state or region. The stored jurisdiction level follows from that choice.
export type Location = { country: string; region: string };

// US patents and trademarks are federal law; other topics default to the
// country as a whole until a state is chosen.
const FEDERAL_TOPICS = new Set(["patent", "trademark"]);

export function toJurisdiction(
  topic: string,
  { country, region }: Location
): Jurisdiction {
  if (!country) return { level: "unsure" };
  const countryCode = country.toUpperCase();
  if (region.trim())
    return { level: "state_region", countryCode, region: region.trim() };
  if (countryCode === "US" && FEDERAL_TOPICS.has(topic))
    return { level: "federal", countryCode };
  return { level: "country", countryCode };
}

export function fromJurisdiction(j: Jurisdiction): Location {
  if (j.level === "unsure") return { country: "", region: "" };
  return {
    country: j.countryCode,
    region: j.level === "state_region" ? j.region : "",
  };
}

/** Same place, whatever level an older intake stored for it. */
export function sameLocation(a: Location, b: Location) {
  return (
    a.country === b.country &&
    a.region.trim().toLocaleLowerCase() === b.region.trim().toLocaleLowerCase()
  );
}

const TOPIC_WORDS: Record<string, RegExp> = {
  contracts:
    /\b(contracts?|agreements?|clauses?|nda|terminat(e|ion)|breach|lease|signed|sign)\b/gi,
  trademark:
    /\b(trademarks?|brand(ing)?|slogan|business name|register (the|my|our) name|uspto)\b/gi,
  patent:
    /\b(patents?|patentable|inventions?|invent(ed|or)?|prior art|provisional)\b/gi,
  employment:
    /\b(employ(er|ee|ment)|fired|layoff|wages?|overtime|non-?compete|harassment|severance)\b/gi,
  tax: /\b(tax(es)?|irs|deductions?|1099|w-?2|audit)\b/gi,
  business:
    /\b(llc|corporation|incorporat(e|ion)|shareholders?|partnership|startup|equity|founders?)\b/gi,
};

/** The topic whose words appear most often, or "other" when none match. */
export function guessTopic(text: string, available: string[]): string {
  let best = "other",
    bestCount = 0;
  for (const [topic, words] of Object.entries(TOPIC_WORDS)) {
    if (!available.includes(topic)) continue;
    // Repeating "logo" or a brand name must not outweigh an explicit contract concern.
    const count = new Set(
      (text.match(words) || []).map(word => word.toLowerCase())
    ).size;
    if (count > bestCount) {
      best = topic;
      bestCount = count;
    }
  }
  return best;
}

export const US_STATES = [
  "Alabama",
  "Alaska",
  "Arizona",
  "Arkansas",
  "California",
  "Colorado",
  "Connecticut",
  "Delaware",
  "District of Columbia",
  "Florida",
  "Georgia",
  "Hawaii",
  "Idaho",
  "Illinois",
  "Indiana",
  "Iowa",
  "Kansas",
  "Kentucky",
  "Louisiana",
  "Maine",
  "Maryland",
  "Massachusetts",
  "Michigan",
  "Minnesota",
  "Mississippi",
  "Missouri",
  "Montana",
  "Nebraska",
  "Nevada",
  "New Hampshire",
  "New Jersey",
  "New Mexico",
  "New York",
  "North Carolina",
  "North Dakota",
  "Ohio",
  "Oklahoma",
  "Oregon",
  "Pennsylvania",
  "Puerto Rico",
  "Rhode Island",
  "South Carolina",
  "South Dakota",
  "Tennessee",
  "Texas",
  "Utah",
  "Vermont",
  "Virginia",
  "Washington",
  "West Virginia",
  "Wisconsin",
  "Wyoming",
];

/** Suggest a state only when the question names one unambiguous US state. */
export function guessLocation(text: string): Location | null {
  const states = US_STATES.filter(state =>
    new RegExp(`\\b${state}\\b`, "i").test(text)
  );
  return states.length === 1 ? { country: "US", region: states[0] } : null;
}
